import { Store } from '@ngrx/store';
import { isUndefined } from 'lodash';
import { isEqual } from 'lodash';
import { ColorReplaceFilter } from 'pixi-filters';
import {
  Sprite,
  Texture,
  Graphics,
  Text,
  TextStyle,
  Rectangle,
  Renderer,
  Container,
  Spritesheet,
  ISpritesheetData,
  DisplayObject,
} from 'pixi.js';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { pairwise, takeUntil, map, distinctUntilChanged, take, startWith, filter, tap } from 'rxjs/operators';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { delayFor } from 'app/core/utilities/async-utils';
import { safeNumber } from 'app/core/utilities/number';
import { formatUserName, getUserInitials, UserUtil } from 'app/core/utilities/user-util';
import { LogService } from 'app/services/logging.service';
import { AppState, Interval, Timeout } from 'app/types';
import { AppModuleServices } from 'app/utils/app-module-util';
import assertDefined from 'app/utils/assert-defined';
import { DUMMY_VIDEO_STREAM } from 'app/utils/media/stream';
import { notNil } from 'app/utils/stream-util';
import SocialTheme from 'entity/SocialTheme';
import User from 'entity/User';

import { ALPHA_MASK, COLOR_INACTIVE_GREY, FONT_FAMILY } from '../constants';
import { COLORS, COLOR_WARNING, COLOR_WHITE, COLOR_NAVY, PLAYER_RADIUS, PLAYER_SIZE, RING_ALPHA } from '../constants';
import { BadgingService } from '../services/badging/badging.service';
import { BrowserService } from '../services/browser.service';
import { ChatService } from '../services/chat.service';
import { EmoteService, UserEmote } from '../services/emote/emote.service';
import { RtcStatsService } from '../services/rtc-stats.service';
import { actions, socialAppActions, socialAppSelectors } from '../state/app';
import { mediaSelectors } from '../state/media';
import { strToHex } from '../utils/theme';

import { createAvatarSprite } from './avatar-sprite';
import { createCursorDirectionIndicator } from './direction-indicator';
import { Emote } from './emote';
import { FallbackVideoCanvas } from './FallbackVideoCanvas';
import { MuteOverlaySprites } from './mute-overlay';
import { DisconnectedOverlay } from './player-components/disconnected-overlay';
import { GroupActivityOverlay } from './player-components/group-activity-overlay';
import { NetworkConnectionBadge } from './player-components/network-connection-badge';
import PlayerGraphics from './player-components/player-graphics';
import { addBlur } from './player-components/player.helper';
import { ConnectionStatus, PlayerStateMachine } from './player-components/player.state';
import { PlayerGroupInfo } from './player-components/player.types';
import { PresenterOverlay } from './player-components/presenter-overlay';
import PlayerUI from './player-ui';
import { VideoFilterService } from '../services/video-room/video-filter.service';
import { ClusterCenter, ParticipantMovement } from 'messages/websocket.messages';

export default interface UnsubscribeListener {
  unsubscribed(): void;
}

interface PlayerSound {
  echoDataArray: Uint8Array;
  analyser: AnalyserNode;
  echoSize: number;
  echoAlpha: number;
  lastEchoAmp: number;
  audioSource?: MediaStreamAudioSourceNode;
}

export class Player extends PlayerGraphics implements UnsubscribeListener {
  public user: User;
  public ringColor?: string;
  public videoElement?: HTMLVideoElement;
  public audioElement?: HTMLAudioElement;
  protected videoSprite?: Sprite;
  public isUserPresenting = false;

  private newVisualIndicators = false;

  private echo?: Graphics;
  private theme?: SocialTheme;

  public distance = 0;
  public targetX?: number;
  public targetY?: number;

  //Group information
  public isInGroup = false;
  public groupSoftLimitExceeded = false;
  public groupHardLimitExceeded = false;
  public onlyOwnGroupRings = false;

  // Only reference with the mainPlayer
  // For participants reference playerGroupInfo
  public groupId?: number;
  public groupRing?: Graphics;
  public groupWarning = false;
  public inSag = false;
  public isGroupSag = false;
  public cursorIndicator?: Container;
  public sound?: PlayerSound;
  private browserService: BrowserService;
  public chatService: ChatService;
  public badgingService: BadgingService;
  public emoteService: EmoteService;
  public rtcStatsService: RtcStatsService;
  public playerInView = new BehaviorSubject<boolean>(false);

  // Handle Player state
  public micEnabled = true;
  public videoEnabled = true;

  // Participant
  public remoteMicEnabled = true;
  public remoteVideoEnabled = true;

  // Audio Real State
  public isAudioEnabled = true;

  private waitForAudioElementInterval?: Interval;
  protected logger: LogService;
  protected readonly logContext = { class: 'Player' };
  private fallbackVideoCanvas?: FallbackVideoCanvas;

  public store: Store<AppState>;
  private destroyed$ = new ReplaySubject<boolean>(1);

  // Primary Player
  public isPrimaryPlayer = false;

  // Overlays
  private muteOverlaySprites?: MuteOverlaySprites;
  private presenterOverlay?: PresenterOverlay;
  private disconnectedOverlay?: DisconnectedOverlay;
  private groupActivityOverlay?: GroupActivityOverlay;

  // Badges
  private networkConnectionBadge?: NetworkConnectionBadge;

  private stateMachine: PlayerStateMachine;
  private playerCreated = false;
  public playerGroupInfo?: PlayerGroupInfo;

  public isMoving = false;
  public isMovingTimeout?: Timeout;
  public isMovingTimeoutDuration = 1000;

  public renderer: Renderer;
  public userName: string | undefined;
  public nameTagContainer?: PlayerUI;
  private blackCheckCanvas: HTMLCanvasElement | undefined;
  private videoIsBlack = false;
  private emoteTimeoutDuration = 10000;
  private emoteTimeout?: NodeJS.Timeout;
  public wrapperContainer?: Container;
  protected videoSpriteContainer!: Container;
  private lastPresenterOverlayVisible?: boolean;
  private playerCreating = false;
  private detectBlackCircles = false;
  private detectBlackCirclesInterval: Interval | undefined;
  public playerVisibility = new Subject<boolean>();
  public playerVisibility$ = this.playerVisibility.asObservable();
  private sprites: Spritesheet<ISpritesheetData>;
  private wasUserPresenting = false;
  private fixedSizeVideo = false;
  public showCursorIndicator?: boolean;
  public isOnboardingPlayer?: boolean;
  private isOnboardingMockPlayerOpened = false;
  private pixiFilters = false;
  // is the createPlayer function already running
  private isCreatingPlayer = false;
  private videoFilterService?: VideoFilterService;
  private boundFilterChangedListener: EventListener | undefined;

  public _playerSagInfo?: { p?: ParticipantMovement; c?: ClusterCenter };
  private showSagDebuggerStats = false;

  public get userId(): string {
    assertDefined(this.user.userId);
    return this.user.userId;
  }

  public constructor({
    badgingService,
    bounds,
    browserService,
    chatService,
    emoteService,
    isOnboardingPlayer = false,
    isPrimary = false,
    renderer,
    ringColor,
    rtcStatsService,
    showCursorIndicator = true,
    sprites = {} as Spritesheet<ISpritesheetData>,
    store,
    user,
    videoElement,
    videoEnabled = true,
    x,
    y,
  }: {
    store: Store<AppState>;
    renderer: Renderer;
    user: User;
    ringColor?: string;
    isPrimary: boolean;
    browserService: BrowserService;
    chatService: ChatService;
    badgingService: BadgingService;
    rtcStatsService: RtcStatsService;
    videoElement?: HTMLVideoElement;
    x?: number;
    y?: number;
    videoEnabled?: boolean;
    bounds?: Rectangle;
    emoteService: EmoteService;
    sprites?: Spritesheet<ISpritesheetData>;
    showCursorIndicator?: boolean;
    isOnboardingPlayer?: boolean;
  }) {
    super();

    this.name = `Player-${user.firstName}-${user.lastName}-${user.userId}`;
    this.sprites = sprites;
    this.eventMode = 'dynamic';
    this.cursor = this.isPrimaryPlayer ? 'default' : 'pointer';

    this.renderer = renderer;
    this.visible = false;
    this.logger = AppModuleServices.injector.get<LogService>(LogService);
    this.browserService = browserService;
    this.chatService = chatService;
    this.rtcStatsService = rtcStatsService;
    this.badgingService = badgingService;
    this.emoteService = emoteService;
    this.width = PLAYER_SIZE;
    this.height = PLAYER_SIZE;
    this.user = user;
    this.ringColor = ringColor;
    this.videoElement = videoElement;
    this.isPrimaryPlayer = isPrimary;
    this.stateMachine = new PlayerStateMachine(store, this.userId);
    this.position.x = safeNumber(x);
    this.position.y = safeNumber(y);
    this.store = store;
    this.videoEnabled = videoEnabled;
    this.showCursorIndicator = showCursorIndicator;
    this.isOnboardingPlayer = isOnboardingPlayer;

    this.store
      .select(socialAppSelectors.getTheme)
      .pipe(take(1), takeUntil(this.destroyed$))
      .subscribe((theme) => {
        this.theme = theme;
        // TODO: we were double tapping non-players here, so we need to refactor this
        // PREVIOUSLY: Only create primary player and players that are visible.
        // if (isPrimary || (bounds && this.isPlayerWithinBounds(safeNumber(x), safeNumber(y), bounds))) {
        if (isPrimary) {
          void this.createPlayer();
        }
      });

    if (isOnboardingPlayer) {
      this.store
        .select(socialAppSelectors.toggleOnboardingMockBadge)
        .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
        .subscribe((toggleOnboardingMockBadge) => (this.isOnboardingMockPlayerOpened = toggleOnboardingMockBadge));
    }

    this.badgingService.badgeNames$.pipe(takeUntil(this.destroyed$)).subscribe((badgeNames) => {
      if (badgeNames[this.userId] && this.nameTagContainer?.nameTag) {
        this.userName = badgeNames[this.userId].trim();
        if (this.userName) {
          this.nameTagContainer.nameTag = UserUtil.trimName(15, this.userName);
        }
      }
    });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.fixed_size_video_bubble)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((fixedSizeVideo) => (this.fixedSizeVideo = fixedSizeVideo));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.visual_indicators)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((newVisualIndicators) => (this.newVisualIndicators = newVisualIndicators));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.detect_black_circles)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((detectBlackCircles) => (this.detectBlackCircles = detectBlackCircles));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.only_own_group_rings)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((onlyOwnGroupRings) => (this.onlyOwnGroupRings = onlyOwnGroupRings));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.pixi_filters)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((pixiFilters) => (this.pixiFilters = pixiFilters));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.video_filters_enabled)
      .pipe(
        filter((videoFiltersEnabled) => videoFiltersEnabled),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        this.videoFilterService = AppModuleServices.injector.get<VideoFilterService>(VideoFilterService);
        this.setupFilterListener();
      });

    this.emoteService.emoteStream$
      .pipe(
        filter((emote) => emote.userId === this.userId),
        tap((userEmote) => {
          this.updateEmote(userEmote);
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe();

    this.stateMachine.stateChange$.pipe(takeUntil(this.destroyed$)).subscribe(this.onPlayerStateChange.bind(this));

    this.store
      .select(mediaSelectors.isVideoEnabled)
      .pipe(startWith(true), pairwise(), takeUntil(this.destroyed$))
      .subscribe(([prevEnabled, currentEnabled]) => this.togglePlayerVideo([prevEnabled, currentEnabled]));

    this.store
      .select(mediaSelectors.isMicEnabled)
      .pipe(startWith(true), pairwise(), takeUntil(this.destroyed$))
      .subscribe(([prevEnabled, currentEnabled]) => this.togglePlayerAudio([prevEnabled, currentEnabled]));

    this.store
      .select(socialAppSelectors.getPresenterIds)
      .pipe(
        map((ids) => ids.includes(this.userId)),
        distinctUntilChanged(isEqual),
        takeUntil(this.destroyed$),
      )
      .subscribe(this.handlePlayerPresenting.bind(this));

    this.store
      .select(socialAppSelectors.showSagDebuggerStats)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((showSagDebuggerStats) => {
        this.showSagDebuggerStats = showSagDebuggerStats;

        if (!showSagDebuggerStats) {
          const container = this.wrapperContainer?.getChildByName('sag-stats-container');
          if (container) {
            container.visible = false;
          }
        }
      });
  }

  /**
   * Set up listener for filter change events
   */
  private setupFilterListener(): void {
    this.boundFilterChangedListener = this.onFilterChanged.bind(this) as EventListener;
    document.addEventListener('filter-changed', this.boundFilterChangedListener);
  }

  private onFilterChanged(event: CustomEvent) {
    if (this.videoFilterService && this.videoSprite && this.userId === event.detail.userId) {
      const { filterType, intensity } = event.detail;

      if (this.videoFilterService.isBackgroundFilter(filterType) && this.videoElement) {
        this.logger.info(this.logContext, 'onFilterChanged: Applying background filter via service.');
        this.videoFilterService
          .applyVideoFilter(this.videoSprite, filterType, this.videoElement, this.renderer)
          .catch((err) => {
            // Log the error safely to avoid crashing the logger
            this.logger.error('[Player] Raw error applying background filter:', err);
            this.logger.error(
              this.logContext,
              'Error applying background filter (see console for raw error)',
              err instanceof Error ? err.message : String(err),
            );
          });
      } else {
        // Let VideoFilterService handle stopping the loop internally
        this.videoFilterService.applyFilter(this.videoSprite, filterType, intensity);
      }
    }
  }

  public healthCheck() {
    // First check if the player should be visible at all
    if (!this.visible) {
      return;
    }

    const wrapperContainer = this.getChildByName('wrapper-container') as Container;

    // Comprehensive check for all components that should be present
    const needsCreation =
      !wrapperContainer ||
      !wrapperContainer.children?.length ||
      // do not check for videoSprite as it is not always created
      // !this.videoSprite ||
      !this.groupRing ||
      !this.nameTagContainer;

    if (needsCreation) {
      // Log the reason we're recreating the player for debugging purposes
      const reason = !wrapperContainer
        ? 'missing wrapperContainer'
        : !wrapperContainer.children?.length
          ? 'empty wrapperContainer'
          : !this.groupRing
            ? 'missing groupRing'
            : !this.nameTagContainer
              ? 'missing nameTagContainer'
              : 'unknown';

      this.logger.info(this.logContext, `Player ${this.userId} needs recreation: ${reason}`);

      // If the player is already being created, don't trigger another creation
      if (this.isCreatingPlayer) {
        this.logger.warn(this.logContext, `Player ${this.userId} creation already in progress`);
        return;
      }

      // Ensure video element is properly restored if needed
      if (this.videoEnabled && this.videoElement && this.videoElement.srcObject === null) {
        // The video source was nulled during culling, attempt to restore it
        this.logger.info(this.logContext, `Restoring video for player ${this.userId}`);
        try {
          // This will trigger a re-fetch of the video stream if needed
          // through the normal video element update mechanisms
          this.enableVideo(true);
        } catch (e) {
          this.logger.error(this.logContext, 'Error restoring video on health check', e);
        }
      }

      // Create the player visual components
      void this.createPlayer();
    }
  }

  public async createPlayer() {
    // Check if creation is already in progress
    if (this.isCreatingPlayer) {
      this.logger.warn(this.logContext, `createPlayer called for ${this.userId} while already in progress.`);
      return;
    }

    // Set the flag
    this.isCreatingPlayer = true;
    try {
      // if the user has updated their name be sure to update the name tag
      this.badgingService.getUserBadgeData(this.userId);

      const themePrimary = this.primaryColor === COLOR_WHITE ? this.primaryColor : this.secondaryColor;

      const wrapperContainer = this.getChildByName('wrapper-container');
      if (wrapperContainer) {
        this.removeChild(wrapperContainer);
      }

      // Initialize WrapperSprite
      this.wrapperContainer = new Container();
      this.addChild(this.wrapperContainer);
      this.wrapperContainer.name = 'wrapper-container';
      this.wrapperContainer.sortableChildren = true;
      // This sound echo goes behind everything in wrapper.
      this.echo = new Graphics();
      this.echo.name = 'echo';
      this.echo.eventMode = 'none';
      this.wrapperContainer.addChild(this.echo);

      const bgColor = UserUtil.getProfileColorInt(this.user);
      const thisUserInitials = getUserInitials(this.user).toUpperCase();
      const initials = new Text(
        thisUserInitials,
        new TextStyle({
          fontFamily: FONT_FAMILY,
          fontSize: 100,
          fill: COLOR_WHITE,
          align: 'center',
        }),
      );
      initials.name = 'initials';
      initials.anchor.set(0.5, 0.5);

      // Apply blur to the initials text
      addBlur(initials, this.pixiFilters);

      const initialsCircle = new Graphics()
        .beginFill(bgColor)
        .drawCircle(0, 0, PLAYER_SIZE / 2)
        .endFill()
        .lineStyle(0, themePrimary);

      // TODO: This is a temporary fix to avoid jest mocking error
      let initialsCircleSmooth;
      if (this.renderer && this.renderer.generateTexture) {
        const initialsTexture = this.renderer.generateTexture(initialsCircle);
        initialsCircleSmooth = Sprite.from(initialsTexture);
        initialsCircleSmooth.name = 'initials-circle';
        initialsCircleSmooth.anchor.set(0.5, 0.5);
      }

      const avatarBorder = new Graphics();
      if (!this.newVisualIndicators) {
        avatarBorder.name = 'avatar-border';
        avatarBorder
          .drawCircle(0, 0, PLAYER_SIZE / 2)
          .lineStyle(0, this.isPrimaryPlayer ? themePrimary : COLORS.participantBorder);
      }

      const { profileImageURI } = this.user;
      const avatarSprite = await createAvatarSprite(this.pixiFilters, profileImageURI).catch((e) => {
        this.logger.error(this.logContext, 'Could not create avatar sprite', e);
      });

      initials.visible = profileImageURI === undefined;

      const emoji = new Emote(PLAYER_SIZE, this.pixiFilters);
      this.muteOverlaySprites = new MuteOverlaySprites(PLAYER_SIZE, this.sprites);
      this.presenterOverlay = new PresenterOverlay(PLAYER_SIZE, this.sprites);
      this.disconnectedOverlay = new DisconnectedOverlay(PLAYER_SIZE, this.sprites);
      this.groupActivityOverlay = new GroupActivityOverlay(PLAYER_SIZE, this.sprites);
      this.networkConnectionBadge = new NetworkConnectionBadge(
        {
          playerRadius: PLAYER_RADIUS,
          stateMachine: this.stateMachine,
          store: this.store,
        },
        this.sprites,
        this.pixiFilters,
      );

      if (initialsCircleSmooth) {
        [initialsCircleSmooth, initials, avatarSprite as DisplayObject]
          .filter(notNil)
          .forEach((child, index) =>
            this.addOrError(this.wrapperContainer as Container, child, `${index} createPlayer`),
          );
      }
      if (!this.newVisualIndicators) {
        this.addOrError(this.wrapperContainer as Container, avatarBorder, `createPlayer`);
      }
      [
        this.videoSpriteContainer,
        this.createCircleBorder(),
        this.muteOverlaySprites.overlay,
        this.presenterOverlay.overlay,
        this.disconnectedOverlay.overlay,
        this.groupActivityOverlay.overlay,
        this.networkConnectionBadge,
        emoji,
      ]
        .filter(notNil)
        .forEach((child, index) => this.addOrError(this.wrapperContainer as Container, child, `${index} createPlayer`));

      // add group ring
      if (this.wrapperContainer) {
        this.buildGroupRing();
      }

      // # region Create Non Primary Objects

      this.nameTagContainer = new PlayerUI(this, this.pixiFilters, this.sprites);
      this.nameTagContainer.userName = UserUtil.trimName(15, this.userName || formatUserName(this.user));
      this.wrapperContainer.addChild(this.nameTagContainer);

      // #endregion

      if (this.isPrimaryPlayer && this.showCursorIndicator) {
        this.cursorIndicator = createCursorDirectionIndicator(
          this.renderer,
          this.newVisualIndicators ? this.convertRingColor(this.ringColor) : this.primaryColor,
        );
        this.wrapperContainer.addChildAt(this.cursorIndicator, 0);
      }

      // Video
      if (this.videoElement) {
        try {
          await this.setVideoElement(this.videoElement);
        } catch (e) {
          this.logger.error('Error setting video element', e);
        }

        if (this.videoSprite) {
          const circleMaskRadius = PLAYER_SIZE / 2;
          const mask = new Graphics();
          mask.name = 'circle-mask-2';
          // FIXME: this is a temporary fix to avoid white circles in chrome
          // when entering the room with the video muted
          mask.alpha = ALPHA_MASK;
          mask.beginFill(COLORS.maskColor);
          mask.drawCircle(0, 0, circleMaskRadius);
          mask.endFill();

          this.videoSpriteContainer.mask = mask;
          this.videoSpriteContainer.addChild(this.videoSprite, mask);
        } else {
          this.logger.error(this.logContext, 'Video sprite not found');
        }
      }

      // Black frame detection
      if (this.detectBlackCircles) {
        this.blackCheckCanvas = document.createElement('canvas');
        if (!this.blackCheckCanvas) {
          this.logger.error('Could not create black check canvas.');
          return;
        }
        this.blackCheckCanvas.id = 'black-check-canvas';
        this.blackCheckCanvas.width = PLAYER_SIZE;
        this.blackCheckCanvas.height = PLAYER_SIZE;
        this.blackCheckCanvas.style.display = 'none';
        this.blackCheckCanvas.style.position = 'absolute';
        this.blackCheckCanvas.style.top = '0';
        this.blackCheckCanvas.style.left = '0';
        document.body.appendChild(this.blackCheckCanvas);
        this.detectBlackCirclesInterval = setInterval(() => {
          if (this.videoElement && this.videoEnabled && this.isBlackFrame(this.videoElement, this.blackCheckCanvas)) {
            this.logger.error('Detected a black frame.');
            // TODO: self healing logic
            this.videoIsBlack = true;
          } else {
            this.videoIsBlack = false;
          }
        }, 500);
      }

      this.playerCreated = true;
      if (notNil(this.playerGroupInfo)) {
        const { groupId, isInGroup, isPrimaryGroup, warning } = this.playerGroupInfo;
        this.toggleGroup({
          groupId,
          isInGroup,
          isPrimaryGroup,
          warning,
        });
      }

      // FIXME: This is a temporary fix to avoid state not being fully settled and mute not coming through from welcome screen, find out why
      await delayFor(1000);
      void this.onPlayerStateChange();
    } catch (error) {
      this.logger.error(this.logContext, 'Error creating player', error);
    } finally {
      // Reset the flag regardless of success or failure
      this.isCreatingPlayer = false;
    }
  }

  private buildGroupRing() {
    if (this.getChildByName('group-ring')) {
      return;
    }
    const groupGraphic = new Graphics().beginFill(COLOR_WHITE, 0.3).drawCircle(0, 0, 150).endFill();

    if (this.onlyOwnGroupRings) {
      groupGraphic.visible = this.isInGroup && !!this.playerGroupInfo?.isPrimaryGroup;
    } else {
      groupGraphic.visible = this.isInGroup;
    }
    groupGraphic.name = 'group-ring';
    groupGraphic.eventMode = 'none';
    this.groupRing = groupGraphic;
    this.wrapperContainer?.addChildAt(groupGraphic, 0);
    return groupGraphic;
  }

  public updateEmote(userEmote: UserEmote): void {
    clearTimeout(this.emoteTimeout);
    const emoteText = this.wrapperContainer?.getChildByName('emote') as Text;
    if (!userEmote.emote || !emoteText) {
      return;
    }
    emoteText.visible = true;
    emoteText.text = userEmote.emote.native;
    this.emoteTimeout = setTimeout(() => {
      emoteText.visible = false;
    }, this.emoteTimeoutDuration);
  }

  /**
   * Destroys the user's visual representation.
   * Used for culling players when they're out of view to improve performance.
   * The player will be rebuilt when it comes back into view.
   */
  public destroyUser() {
    if (this.wrapperContainer) {
      this.logger.debug(this.logContext, `Destroying user ${this.userId} visual components`);

      // First pause video to prevent unnecessary processing
      if (this.videoElement) {
        try {
          // Stop video playing to reduce CPU/GPU usage when culled
          this.videoElement.pause();
          // Clear the video source to free up memory
          this.videoElement.srcObject = null;
          // Remove video event listeners to prevent memory leaks
          this.videoElement.onloadedmetadata = null;
          this.videoElement.oncanplay = null;
          this.videoElement.onerror = null;
        } catch (e) {
          this.logger.error(this.logContext, 'Error while cleaning up video element', e);
        }
      }

      // Properly destroy video sprite and its textures
      if (this.videoSprite) {
        try {
          // Ensure texture is properly released
          if (this.videoSprite.texture) {
            this.videoSprite.texture.destroy(true);
          }
          this.videoSprite.destroy({ children: true, texture: true, baseTexture: true });
          this.videoSprite = undefined;
        } catch (e) {
          this.logger.error(this.logContext, 'Error destroying video sprite', e);
        }
      }

      // Handle all visual components
      if (this.nameTagContainer) {
        // Just hide it, don't destroy (expensive to rebuild)
        this.nameTagContainer.hide();
      }

      if (this.groupRing) {
        // Hide ring but don't destroy (expensive to rebuild)
        this.groupRing.visible = false;
      }

      // Handle sound/audio resources
      if (this.sound) {
        try {
          // Release audio resources
          this.sound.audioSource?.disconnect();
        } catch (e) {
          this.logger.error(this.logContext, 'Error disconnecting audio source', e);
        }
      }

      try {
        // Remove all children with proper destroy flags
        this.wrapperContainer.removeChildren().forEach((child) => {
          if (!child.destroyed) {
            child.destroy({ children: true, texture: true, baseTexture: true });
          }
        });
        this.removeChild(this.wrapperContainer);
      } catch (e) {
        this.logger.error(this.logContext, 'Error removing wrapper container children', e);
      }

      this.wrapperContainer = undefined;

      // Reset flags to allow proper recreation
      this.isCreatingPlayer = false;
    }
  }

  public updateRingColor(ringColor: string) {
    this.ringColor = ringColor;
    if (this.theme) {
      void this.updateTheme(this.theme);
    }
  }

  // TODO: trigger this on zoom finished
  public adjustNameTagPositionAndScale(zoom: number): void {
    if (!this.nameTagContainer) {
      return;
    }
    const newScale = zoom !== 0 ? 1 / zoom : 1;
    this.nameTagContainer.scale.set(newScale);
    this.nameTagContainer.onZoom(zoom);
  }

  public toggleBadgeOpen(open: boolean) {
    const { userId } = this.user;
    this.store.dispatch(
      socialAppActions.TOGGLE_BADGE_OPEN({ open, userId: userId ? userId : '', isSelectedInViewport: true }),
    );
  }

  public toggleOnboardingMockBadge() {
    this.store.dispatch(
      actions.TOGGLE_ONBOARDING_MOCK_BADGE({ openOnboardingMockBadge: !this.isOnboardingMockPlayerOpened }),
    );
  }

  private async onPlayerStateChange(): Promise<void> {
    const {
      disconnectedOverlayVisible,
      groupActivityOverlayVisible,
      micBadgeVisible,
      presenterOverlayVisible,
      videoAndMicOverlayVisible,
      videoOverlayVisible,
    } = this.stateMachine;

    // Presentation state
    if (this.presenterOverlay && this.lastPresenterOverlayVisible !== presenterOverlayVisible) {
      // cache the last presenter overlay visible state to avoid mutation
      this.lastPresenterOverlayVisible = presenterOverlayVisible;
      if (!presenterOverlayVisible) {
        // Delay the presenter overlay to prevent flickering
        await delayFor(1000);
      }
      this.presenterOverlay!.visible = presenterOverlayVisible;
    }

    // Audio State
    if (this.nameTagContainer) {
      this.nameTagContainer.micOffIndicator!.visible = micBadgeVisible;
      this.nameTagContainer.updatePlayer(this);
    }

    // Video and Audio State
    if (this.muteOverlaySprites && this.muteOverlaySprites.videoOverlayIconBig) {
      // Set large video overlay
      this.muteOverlaySprites.videoOverlayIconBig.visible =
        (videoOverlayVisible || this.videoIsBlack) && !videoAndMicOverlayVisible;
      // Set video and mic overlay
      this.muteOverlaySprites.videoOverlayIcon.visible = videoAndMicOverlayVisible;
      this.muteOverlaySprites.micOverlayIcon.visible = videoAndMicOverlayVisible;
    }

    if (this.videoSprite) {
      // if the presenter is the primary player, wait and make visible later
      this.videoSprite.visible = this.stateMachine.videoVisible && (!this.wasUserPresenting || !this.isPrimaryPlayer);
    }

    // Connection State
    if (this.disconnectedOverlay) {
      this.disconnectedOverlay.visible = disconnectedOverlayVisible || this.videoIsBlack;
    }
    // Group Activity State
    if (this.groupActivityOverlay) {
      this.groupActivityOverlay.visible = groupActivityOverlayVisible;
    }
  }

  public setConnectionStatus(connectionStatus: ConnectionStatus): void {
    const { state } = this.stateMachine;
    this.stateMachine.setState({ ...state, connectionStatus });
  }

  /**
   * Presentation state
   *
   * @param isUserPresenting Whether this player is presenting or not.
   */
  private async handlePlayerPresenting(isUserPresenting: boolean): Promise<void> {
    if (!this.user) {
      return;
    }
    if (isUserPresenting) {
      this.isUserPresenting = isUserPresenting;
      this.startPresentation();
    } else if (this.isUserPresenting && !isUserPresenting) {
      this.isUserPresenting = isUserPresenting;
      void this.endPresentation();
      this.wasUserPresenting = true;
      await delayFor(1000);
      this.wasUserPresenting = false;
    }
  }

  private startPresentation(): void {
    this.hideVideoSprite();
    const { state } = this.stateMachine;
    this.stateMachine.setState({ ...state, isPresenting: true });
  }

  private async endPresentation(): Promise<void> {
    const { state } = this.stateMachine;
    this.stateMachine.setState({ ...state, isPresenting: false });
    await delayFor(1000);
    // this.displayVideoSprite();
    void this.videoElement?.play();
  }

  // triggered by a listener in scene-manager
  public handlePointerOver(zoom: number) {
    this.adjustNameTagPositionAndScale(zoom);
    this.nameTagContainer?.show(zoom);

    if (this.isPrimaryPlayer && this.cursorIndicator) {
      this.cursorIndicator.visible = false;
      return;
    }

    // Notify the user ID to the rtcStatsService
    this.rtcStatsService.pointerOverUser(this.userId);
  }

  // triggered by a listener in scene-manager
  public handlePointerOut() {
    this.nameTagContainer?.hide();
    if (this.isPrimaryPlayer && this.cursorIndicator) {
      this.cursorIndicator.visible = true;
      return;
    }

    // Clean user ID from the rtcStatsService
    this.rtcStatsService.pointerOutUser();
  }

  protected async createVideoTexture({
    element,
    height,
    width,
  }: {
    element: HTMLVideoElement;
    width: number;
    height: number;
  }): Promise<Texture> {
    // if (this.useFallbackCanvas) {
    //   this.logger.warn(this.logContext, 'Using fallback canvas video renderer for player');
    //   this.fallbackVideoCanvas = new FallbackVideoCanvas(element, width, height);
    //   return this.fallbackVideoCanvas.texture;
    // }
    if (element.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      return Texture.from(element) ?? Texture.EMPTY;
    } else {
      this.logger.warn(this.logContext, 'Video element not ready, waiting for it');
      await new Promise<void>((resolve) => {
        const onLoadedData = () => {
          element.removeEventListener('loadeddata', onLoadedData);
          resolve();
        };
        element.addEventListener('loadeddata', onLoadedData);
      });
      return this.createVideoTexture({ element, width, height });
    }
  }

  public toggleGroup({ groupId, isInGroup, isPrimaryGroup, warning }: PlayerGroupInfo): void {
    // Only reference with the mainPlayer
    // For participants reference playerGroupInfo
    this.playerGroupInfo = {
      groupId,
      isInGroup,
      isPrimaryGroup,
      warning,
    };

    if (!this.groupRing || !this.wrapperContainer) {
      return;
    }
    this.groupRing.clear();
    this.groupWarning = !!warning;

    if (warning) {
      this.groupRing.beginFill(COLOR_WARNING, RING_ALPHA).drawCircle(0, 0, 150).endFill();
      this.groupRing.visible = true;
      this.groupWarning = warning;
      return;
    }

    this.isInGroup = isInGroup;

    if (this.onlyOwnGroupRings) {
      this.groupRing.visible = isInGroup && !!isPrimaryGroup;
    } else {
      this.groupRing.visible = isInGroup;
    }

    this.groupId = groupId ?? undefined;
    this.groupRing.beginFill(COLOR_WHITE, RING_ALPHA).drawCircle(0, 0, 150).endFill();
  }

  private convertRingColor(ringColor?: string): number {
    if (ringColor && ringColor.length > 3 && ringColor.indexOf('#') === 0) {
      return strToHex(ringColor);
    } else {
      return COLOR_INACTIVE_GREY;
    }
  }

  private createCircleBorder() {
    if (!this.sprites.textures) {
      return new Sprite();
    }
    const circle = new Sprite(this.sprites.textures['circle.png']);
    circle.width = PLAYER_SIZE + 6;
    circle.height = PLAYER_SIZE + 6;
    circle.name = 'circle-border';
    circle.eventMode = 'none';
    circle.anchor.set(0.5, 0.5);

    const colorReplaceFilter = new ColorReplaceFilter(0x000000, this.convertRingColor(this.ringColor), 0.1);
    circle.filters = [colorReplaceFilter];

    return circle;
  }

  public updateTheme(theme: SocialTheme) {
    this.theme = theme;
    if (!this.wrapperContainer) {
      // TODO: unnessaray because of health check
      // this.logger.error(this.logContext, 'Wrapper container not found, rebuilding player');
      // await this.createPlayer();
      void this.updateTheme(theme);
      return;
    }

    // Changing border color
    const border = this.wrapperContainer.getChildByName('circle-border');
    if (border) {
      const borderIndex = this.wrapperContainer.getChildIndex(border);
      this.wrapperContainer.removeChildAt(borderIndex);
      const cbs = this.createCircleBorder();
      this.wrapperContainer.addChildAt(cbs, borderIndex);
    }

    // Changing direction indicator color
    if (this.cursorIndicator) {
      const indicatorIndex = this.wrapperContainer.getChildIndex(this.cursorIndicator);
      this.wrapperContainer.removeChild(this.cursorIndicator);
      this.cursorIndicator = createCursorDirectionIndicator(
        this.renderer,
        this.newVisualIndicators ? this.convertRingColor(this.ringColor) : this.primaryColor,
      );
      this.wrapperContainer.addChildAt(this.cursorIndicator, indicatorIndex);
    }
  }

  // all players
  public async setVideoElement(videoElement: HTMLVideoElement) {
    // remove existing video-sprite-container if it exists
    if (this.videoSpriteContainer) {
      this.wrapperContainer?.removeChild(this.videoSpriteContainer);
    }

    this.videoElement = videoElement;

    this.videoSpriteContainer = new Container();
    this.videoSpriteContainer.name = 'video-sprite-container';
    this.videoSpriteContainer.position.set(0, 0); // Explicitly center the container

    this.videoSprite = new Sprite(Texture.EMPTY);
    this.videoSprite.name = 'video-sprite';
    this.videoSprite.anchor.set(0.5, 0.5);
    this.videoSprite.position.set(0, 0);

    const height = PLAYER_SIZE;
    const width = PLAYER_SIZE;
    let pixiTexture = await this.createVideoTexture({ element: this.videoElement, width, height });

    // check if texture is valid
    if (!pixiTexture || !pixiTexture.valid) {
      this.logger.error(this.logContext, 'Invalid video texture');
      pixiTexture = Texture.EMPTY;
    }

    this.videoSprite.texture = pixiTexture;

    // Circle Mask -------------------------------------------------------------
    // the sprite gets scaled based on the image provided so we need this adjusted by same
    const circleMask = new Graphics().beginFill(COLORS.maskColor).drawCircle(0, 0, PLAYER_RADIUS).endFill();
    // FIXME: this is a temporary fix to avoid white circles in chrome
    // when entering the room with the video muted
    circleMask.alpha = ALPHA_MASK;
    circleMask.name = 'circle-mask';

    this.videoSpriteContainer.mask = circleMask;
    this.videoSpriteContainer.addChild(circleMask, this.videoSprite);

    this.applyRatio();
    videoElement.onresize = this.applyRatio;
    videoElement.onplay = this.applyRatio;
    videoElement.addEventListener('pause', async () => {
      await this.videoElement?.play();
    });

    // ---------------------------------------------------------------------------

    if (!this.wrapperContainer) {
      return;
    }

    // TODO: This is a temporary fix to avoid elements not rendering, refactor!
    // Keep the children previously assigned
    // const initialsCircle = this.wrapperContainer.getChildByName('initials-circle');
    // const initials = this.wrapperContainer.getChildByName('initials');
    // let avatarSprite = this.wrapperContainer.getChildByName('avatar-container');
    // if (!avatarSprite) {
    //   this.logger.warn(this.logContext, 'Avatar sprite not found');
    //   avatarSprite = await createAvatarSprite(this.renderer, this.user.profileImageURI);
    //   return;
    // }
    // //
    // let circleBorder = this.wrapperContainer.getChildByName('circle-border');
    // if (!circleBorder) {
    //   this.logger.error(this.logContext, `Circle border not found for ${this.name}`);
    //   circleBorder = this.createCircleBorder();
    // }
    // //
    // let groupRing = this.wrapperContainer.getChildByName('group-ring');
    // if (!groupRing) {
    //   this.logger.error(this.logContext, `groupRing border not found for ${this.name}`);
    //   groupRing = this.buildGroupRing();
    // }
    // //
    // const muteIconsOverlay = this.wrapperContainer.getChildByName('mute-icons-overlay');
    // //
    // let presenterOverlay = this.wrapperContainer.getChildByName('presenter-overlay');
    // if (!presenterOverlay) {
    //   this.logger.error(this.logContext, `Presenter overlay not found for ${this.name}`);
    //   presenterOverlay = new PresenterOverlay(this.renderer, PLAYER_SIZE, this.sprites).overlay;
    // }
    // const disconnectedOverlay = this.wrapperContainer.getChildByName('disconnected-overlay');
    // const groupActivityOverlay = this.wrapperContainer.getChildByName('group-activity-overlay');
    // const emoteText = this.wrapperContainer.getChildByName('emote');

    // // Should delete all the children from wrapperSprite
    // this.wrapperContainer.removeChildren();

    // // Add all the children back
    [
      //   this.echo,
      //   initialsCircle,
      //   initials,
      //   avatarSprite,
      //   muteIconsOverlay,
      //   presenterOverlay,
      this.videoSpriteContainer,
      //   circleBorder,
      //   disconnectedOverlay,
      //   groupActivityOverlay,
      //   this.nameTagContainer,
      //   emoteText,
      //   this.networkConnectionBadge,
    ]
      .filter(notNil)
      .forEach((child, index) =>
        this.addOrError(this.wrapperContainer as Container, child, `${index} setVideoElement`),
      );

    // if (this.isPrimaryPlayer && this.cursorIndicator) {
    //   this.wrapperContainer.addChild(this.cursorIndicator);
    // }

    // if (groupRing) {
    //   this.wrapperContainer.addChildAt(groupRing, 0);
    // }

    // this.wrapperContainer.eventMode = 'static';
    // this.wrapperContainer.cursor = 'pointer';

    // this.removeChildren();
    // this.addChild(this.wrapperContainer);
  }

  // Set the scale again after the video starts playing.
  private applyRatio = (): void => {
    if (!this.videoSprite || !this.videoElement) {
      this.logger.error(this.logContext, 'Could not apply ratio to video sprite');
      return;
    }

    let height = PLAYER_SIZE;
    let width = PLAYER_SIZE;

    if (this.fixedSizeVideo && !this.browserService.isFirefox) {
      this.videoSprite.height = PLAYER_SIZE;
      this.videoSprite.width = PLAYER_SIZE;
      const x = this.isPrimaryPlayer ? -1 : 1;
      this.videoSprite.scale.set(x, 1);
      return;
    } else {
      // OLD COMMENT: Some browsers (firefox) don't supply settings for the video if the video is supplied by a webrtc media track.
      // const newHeightRatio = PLAYER_SIZE / videoElement.videoHeight;
      // const newWidthRatio = PLAYER_SIZE / videoElement.videoWidth;
      // const newRatio = Math.max(newHeightRatio, newWidthRatio);
      // this.videoSpriteContainer.scale.set(newRatio, newRatio);
      // (this.videoSpriteContainer.mask as Sprite).scale.set(1 / newRatio, 1 / newRatio);
      try {
        const res = (this.videoElement.srcObject as MediaStream).getVideoTracks()[0].getSettings();
        height = res?.height ?? height;
        width = res?.width ?? width;

        if (this.browserService.isFirefox) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const media: any = this.videoElement.srcObject;
          height = media?.canvas.height ?? height;
          width = media?.canvas.width ?? width;
        }
      } catch (e) {
        this.logger.warn(this.logContext, 'Could not determine actual video resolution, applyRatio', e);
      }
      const heightRatio = PLAYER_SIZE / height;
      const widthRatio = PLAYER_SIZE / width;
      const ratio = Math.max(heightRatio, widthRatio);
      this.videoSprite.scale.set(ratio * (this.isPrimaryPlayer ? -1 : 1), ratio);
    }
  };

  private addOrError(container: Container, child: DisplayObject | Container | null | undefined, caller: string): void {
    if (child?.name && this.getChildByName(child?.name)) {
      // if the child is already added to the container, do not add it again
      this.logger.error(this.logContext, `child already added: ${caller}`, container);
      return;
    }
    try {
      if (!child) {
        this.logger.error(this.logContext, `Cannot add null or undefined child to container: ${caller}`);
        return;
      }

      if (!container) {
        this.logger.error(this.logContext, `Cannot add child to null or undefined container: ${caller}`);
        return;
      }

      if (!container.children) {
        this.logger.error(this.logContext, `Container has no children array: ${caller}`, container);
        return;
      }

      // Check if the child is already a child of another container
      if (child.parent) {
        this.logger.warn(this.logContext, `Child already has a parent, removing it first: ${caller}`, child.name);
        child.parent.removeChild(child);
      }

      container.addChild(child);
    } catch (e) {
      this.logger.error(this.logContext, `Exception adding element to container: ${caller}`, e, {
        childName: child?.name,
        containerName: container?.name,
      });
    }
  }

  /**
   * Set a new video source for the player.
   * @param stream The video stream for the player.
   */
  public setVideoElementSource(stream: MediaStream) {
    if (!this.videoElement) {
      this.logger.error(this.logContext, 'setVideoElementSource: cannot set source on undefined video element');
      return;
    }
    this.videoElement.pause();
    this.videoElement.srcObject = !this.isUserPresenting ? stream : DUMMY_VIDEO_STREAM;
    if (stream.getVideoTracks()[0].muted) {
      return;
    }

    // restart the video element if it is paused, this happens after presentation ends
    if (this.videoElement.paused && !this.isUserPresenting) {
      this.videoElement.addEventListener('loadeddata', this.onVideoReady.bind(this), { once: true });
    }
  }

  private async onVideoReady() {
    try {
      if (this.videoElement && this.videoElement.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
        await this.waitForVideoReady(this.videoElement);
        await this.videoElement.play();
        // TODO: why is this timeout needed?
        await delayFor(1000);
        this.videoSprite!.visible = true;
      }
    } catch (e) {
      this.logger.error(this.logContext, 'setVideoElementSource: Play failed', e);
    }
  }

  private waitForVideoReady(video: HTMLVideoElement, timeout = 5000): Promise<void> {
    const startTime = Date.now();

    return new Promise((resolve, reject) => {
      const checkReady = () => {
        if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          resolve();
        } else if (Date.now() - startTime < timeout) {
          requestAnimationFrame(checkReady); // Retry next frame
        } else {
          reject(new Error('Video element failed to become ready in time.'));
        }
      };

      checkReady();
    });
  }

  public setPlayerNotMovingTimeout() {
    this.isMoving = true;
    if (this.isMovingTimeout) {
      clearTimeout(this.isMovingTimeout);
    }
    this.isMovingTimeout = setTimeout(() => {
      this.isMoving = false;
    }, this.isMovingTimeoutDuration);
  }

  /* Player Movement */
  public move(bounds: Rectangle): void {
    if (isUndefined(this.targetX) || isUndefined(this.targetY)) {
      this.setDisplayPosition(safeNumber(this.x), safeNumber(this.y), bounds);
      return;
    }

    let x = this.targetX - this.x;
    let y = this.targetY - this.y;

    // Proceed only when difference > 1 because decimals can keep this jiggling forever
    x = this.x + (x < 0 ? Math.floor(x / 8) : Math.ceil(x / 8));
    y = this.y + (y < 0 ? Math.floor(y / 8) : Math.ceil(y / 8));
    this.setDisplayPosition(x, y, bounds);
  }

  public teleport(x: number, y: number): void {
    this.x = x;
    this.y = y;
    this.setTargetPosition(x, y);
  }

  public setTargetPosition(x: number, y: number): void {
    this.targetX = x;
    this.targetY = y;
  }

  public isPlayerWithinBounds(x: number, y: number, bounds: Rectangle): boolean {
    this.x = this.x !== x ? x : this.x;
    this.y = this.y !== y ? y : this.y;

    const leftBound = bounds.x;
    const rightBound = bounds.x + bounds.width;
    const topBound = bounds.y;
    const bottomBound = bounds.y + bounds.height;

    const hidden =
      x + PLAYER_RADIUS < leftBound ||
      x - PLAYER_RADIUS > rightBound ||
      y + PLAYER_RADIUS < topBound ||
      y - PLAYER_RADIUS > bottomBound;
    return !hidden;
  }

  public setDisplayPosition(x: number, y: number, bounds: Rectangle): void {
    const visible = this.isPlayerWithinBounds(safeNumber(x), safeNumber(y), bounds);

    // If player is visible and hasn't been created, create player
    if (visible && !this.playerCreated && !this.playerCreating && !this.isPrimaryPlayer) {
      this.playerCreating = true;
      void this.createPlayer();
    }

    // if main player Object and Sprite get disconnected, re-add it to the roomContainer
    // FIXME: this needs to go elsewhere
    // if (this.sceneManager && this.sceneManager.player && this.userId === this.sceneManager.player.userId) {
    //   const roomContainer = this.sceneManager.roomContainer;
    //   const playerSprite = roomContainer.getChildByLabel(this.sceneManager.player.label ?? '');
    //   if (playerSprite && playerSprite.x !== x) {
    //     roomContainer.removeChild(playerSprite);
    //     roomContainer.addChild(this.sceneManager.player);
    //   }
    // }

    if (visible === this.visible) {
      return;
    }
    this.setPlayerVisibility(visible);
  }

  private setPlayerVisibility(visible: boolean) {
    this.visible = visible;
    if (this.wrapperContainer) {
      this.wrapperContainer.visible = visible;
    }
    // If the player is visible, indicate the player is still in view each time the position changes. This drives
    // relative position calculation, which is used downstream for subscription processing
    this.playerVisibility.next(visible);
  }

  public unsubscribed(): void {
    if (this.videoSprite && this.videoSprite.texture) {
      this.videoSprite.texture = Texture.EMPTY;
    }
  }

  /* Sound Management */
  public setSound(sound: PlayerSound): void {
    this.sound = sound;
  }

  public setVolume(volume: number, isRecheck?: boolean): void {
    if (!isRecheck && this.waitForAudioElementInterval) {
      clearInterval(this.waitForAudioElementInterval);
    }

    if (!this.audioElement) {
      if (!this.waitForAudioElementInterval) {
        this.waitForAudioElementInterval = setInterval(() => this.setVolume(volume, true), 1000);
      }
    } else {
      if (this.waitForAudioElementInterval) {
        clearInterval(this.waitForAudioElementInterval);
      }
      this.audioElement.volume = volume;
      this.store.dispatch(actions.SET_PARTICIPANT_VOLUME({ volume, userId: this.userId }));
    }
  }

  public renderEcho(): void {
    if (!this.playerCreated || !this.echo || !this.sound) {
      return;
    }
    const { echo, sound } = this;
    if (!sound || !echo) {
      return;
    }
    const { analyser, echoDataArray: dataArray, lastEchoAmp } = sound;
    const amplitude = dataArray[0]; // no sound is 128

    // Get the current frequency data from the audio stream.
    analyser.getByteTimeDomainData(dataArray);

    if (lastEchoAmp >= 126 && lastEchoAmp <= 130) {
      sound.lastEchoAmp = amplitude;
      return; // not enough sound to show anything
    }

    // Stop if we've lost our audio source.
    if (!sound.audioSource) {
      return;
    }

    echo.clear();
    echo.beginFill(this.echoColor);

    let modifiedAmp = Math.abs(128 - lastEchoAmp); // get absolute difference from 128 to gauge amplitude of freq.
    modifiedAmp = modifiedAmp > 40 ? 20 : modifiedAmp / 2; // this is just to limit the ring size and then half it.

    if (sound.echoSize < PLAYER_RADIUS + modifiedAmp) {
      // lets reset the ring and start next one
      sound.echoSize += 0.5;
      echo.drawCircle(0, 0, sound.echoSize);
      sound.echoAlpha -= 0.01;
      echo.alpha = sound.echoAlpha;
      if (amplitude > sound.lastEchoAmp) {
        sound.lastEchoAmp = amplitude;
      }
      return;
    }

    echo.drawCircle(0, 0, PLAYER_RADIUS);
    echo.alpha = 0.5;
    sound.lastEchoAmp = amplitude;
    sound.echoSize = PLAYER_RADIUS + 1;
    sound.echoAlpha = 0.4;
  }

  private get primaryColor(): number {
    const { colorPrimary = '' } = this.theme ?? {};
    if (colorPrimary.length > 3 && colorPrimary.indexOf('#') === 0) {
      return strToHex(colorPrimary);
    }
    return COLOR_WHITE;
  }

  private get secondaryColor(): number {
    const { colorSecondary = '' } = this.theme ?? {};
    if (colorSecondary.length > 3 && colorSecondary.indexOf('#') === 0) {
      return strToHex(colorSecondary);
    }
    return COLOR_WHITE;
  }

  private get echoColor(): number {
    if (this.newVisualIndicators) {
      return this.convertRingColor(this.ringColor);
    } else {
      return this.isPrimaryPlayer ? this.primaryColor : this.secondaryColor;
    }
  }

  /* Handle Player Audio */
  private togglePlayerAudio = ([prevEnabled, currentEnabled]: boolean[]): void => {
    if (!this.isPrimaryPlayer) {
      return;
    }

    if (prevEnabled === currentEnabled) {
      return;
    }

    // proceed to Enable/Disable audio
    this.enableMic(currentEnabled);
  };

  public setInitialAudio(enabled: boolean): void {
    //Set initial mic state for player - Avoid some state validations
    this.isAudioEnabled = enabled;
    this.micEnabled = enabled;
    const { state } = this.stateMachine;
    this.stateMachine.setState({ ...state, micEnabled: this.micEnabled, audioEnabled: this.isAudioEnabled });
  }

  public enableMic(enabled: boolean): void {
    //Enable local mic
    this.micEnabled = enabled;
    //Audio is enabled only for the player if user is unmuted locally and remote
    this.isAudioEnabled = this.micEnabled && this.remoteMicEnabled;

    if (this.nameTagContainer) {
      //Update UI
      this.nameTagContainer.micOffIndicator!.visible = !this.micEnabled;
      this.nameTagContainer.updatePlayer(this);
      this.nameTagContainer.updateMicIndicatorIcon();
    } else {
      this.logger.error(this.logContext, 'Name tag container not found');
    }

    if (this.audioElement) {
      this.audioElement.muted = !enabled;
    } else {
      this.logger.error(this.logContext, 'Audio element not found');
    }

    //Update state
    const { state } = this.stateMachine;
    this.stateMachine.setState({ ...state, micEnabled: this.micEnabled, audioEnabled: this.isAudioEnabled });
  }

  // Handle room participant muting mic
  public enableRemoteMic(enabled: boolean): void {
    const { state } = this.stateMachine;

    // Allow remote changes only if local mic is enabled
    if (this.micEnabled) {
      this.isAudioEnabled = enabled;
      this.remoteMicEnabled = enabled;
      if (this.audioElement && this.nameTagContainer) {
        // Update UI
        this.audioElement.muted = !enabled;
        this.nameTagContainer.micOffIndicator!.visible = !this.micEnabled;
        this.nameTagContainer.updatePlayer(this);
        this.nameTagContainer.updateMicIndicatorIcon();
      }
    } else {
      this.remoteMicEnabled = false;
    }

    // Update state
    this.stateMachine.setState({
      ...state,
      audioEnabled: this.isAudioEnabled,
      micEnabled: this.micEnabled,
      remoteMicEnabled: this.remoteMicEnabled,
    });
  }

  /* Handle Player Video */
  private togglePlayerVideo = ([prevEnabled, currentEnabled]: boolean[]): void => {
    if (!this.isPrimaryPlayer) {
      return;
    }

    if (prevEnabled === currentEnabled) {
      return;
    }

    // proceed to Enable/Disable visual
    this.enableVideo(currentEnabled);
  };

  public enableVideo(enabled: boolean): void {
    this.videoEnabled = enabled;

    if (this.videoSpriteContainer) {
      this.videoSpriteContainer.visible = enabled && !this.isUserPresenting;
    }
    if (this.nameTagContainer?.videoButton) {
      this.nameTagContainer.videoButton.icon = enabled ? 'videocam' : 'videocam_off';
    }
    const { state } = this.stateMachine;
    this.stateMachine.setState({ ...state, videoEnabled: this.videoEnabled });
  }

  // Handle room participant muting video
  public enableRemoteVideo(enabled: boolean): void {
    this.remoteVideoEnabled = enabled;

    // Handle mic off indicator after video stream loads
    if (this.nameTagContainer?.micOffIndicator) {
      this.nameTagContainer.micOffIndicator.visible = !this.remoteMicEnabled;
    }

    // Return if participant video is already disabled for player
    if (enabled && !this.videoEnabled && !this.isUserPresenting) {
      return;
    }

    if (this.videoSprite && this.nameTagContainer?.videoButton && this.nameTagContainer.videoButton.ready) {
      this.videoSprite.visible = enabled && !this.isUserPresenting;
      this.nameTagContainer.videoButton.icon = enabled ? 'videocam' : 'videocam_off';
    }

    const { state } = this.stateMachine;
    this.stateMachine.setState({ ...state, videoEnabled: this.remoteVideoEnabled });
  }

  /**
   * Hide video.
   * This is useful for hiding the user video as we switch camera streams, to avoid UI glitches. It is not the same as
   * muting the players video, and should not show a mute indication.
   */
  public hideVideoSprite(): void {
    if (this.videoSprite && this.videoSprite.visible) {
      this.videoElement?.pause();
      this.videoSprite.visible = false;
    }
  }

  /**
   * Restore player video.
   *
   *  When the players video has been hidden by calling hideVideoSprite(), this method should be called to restore it.
   *
   */
  public displayVideoSprite(): void {
    if (!this.remoteVideoEnabled || this.isUserPresenting) {
      return;
    }
    if (this.videoSprite) {
      this.videoSprite.visible = true;
      void this.videoElement?.play();
    }
  }

  private isBlackFrame(videoElement: HTMLVideoElement, canvas: HTMLCanvasElement | undefined): boolean {
    if (!canvas) {
      this.logger.error('Canvas is not available.');
      return false;
    }
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) {
      this.logger.error('Canvas context is not available.');
      return false;
    }

    context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
    const frame = context.getImageData(0, 0, canvas.width, canvas.height);

    // Check if the frame is black
    for (let i = 0; i < frame.data.length; i += 4) {
      if (frame.data[i] > 0 || frame.data[i + 1] > 0 || frame.data[i + 2] > 0) {
        return false;
      }
    }
    return true;
  }

  public destroy(options?: { children?: boolean; texture?: boolean; baseTexture?: boolean }): void {
    // FIXME: is this nessasary?
    // this.sceneManager
    //   .playerInView({ player: this, visible: false })
    //   .catch((e) => this.logger.error(this.logContext, 'Error in destroy > playerInView', e));
    this.playerVisibility.next(false);
    if (this.waitForAudioElementInterval) {
      clearInterval(this.waitForAudioElementInterval);
    }
    if (this.detectBlackCirclesInterval) {
      clearInterval(this.detectBlackCirclesInterval);
    }
    this.waitForAudioElementInterval = undefined;
    this.groupRing = undefined;
    this.videoElement = undefined;
    this.audioElement = undefined;
    this.videoSprite = undefined;
    this.theme = undefined;
    this.sound = undefined;
    this.playerCreated = false;

    this.networkConnectionBadge?.destroyIndicators();
    this.networkConnectionBadge = undefined;

    super.destroy(options);

    if (this.boundFilterChangedListener) {
      document.removeEventListener('filter-changed', this.boundFilterChangedListener);
    }

    this.destroyed$.next(true);
    this.destroyed$.complete();

    this.fallbackVideoCanvas?.destroy();
    this.fallbackVideoCanvas = undefined;

    this.stateMachine.destroy();
  }

  private createSagStatsContainer(): Container {
    const container = new Container();
    container.name = 'sag-stats-container';
    const padding = 10;
    const cornerRadius = 8;
    const background = new Graphics()
      .beginFill(0xffffff, 0.9)
      .lineStyle(1, 0xcccccc)
      .drawRoundedRect(0, 0, 170, 230, cornerRadius)
      .endFill();

    const text = new Text('', {
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      fill: COLOR_NAVY,
      align: 'left',
    });

    text.name = 'sag-stats-text';
    text.position.set(padding, padding);

    container.addChild(background);
    container.addChild(text);
    container.visible = false;
    container.position.set(128, -110);

    return container;
  }

  public updateSagStats(): void {
    if (!this.playerSagInfo || !this.showSagDebuggerStats) {
      const container = this.wrapperContainer?.getChildByName('sag-stats-container');
      if (container) {
        container.visible = false;
      }
      return;
    }

    const { c, p } = this.playerSagInfo;
    let container = this.wrapperContainer?.getChildByName('sag-stats-container') as Container;

    if (!container) {
      container = this.createSagStatsContainer();
      this.wrapperContainer?.addChild(container);
    }

    const text = container.getChildByName('sag-stats-text') as Text;
    text.text = `
      ParticipantMovement
      x: ${p?.x}
      y: ${p?.y}
      sag: ${p?.sag}
      r: ${p?.r}
      d: ${p?.d}
      c: ${p?.c}
      ----------------
      ClusterCenter: 
      c: ${c?.c}
      x: ${c?.x}
      y: ${c?.y}
      sag: ${c?.sag}
    `;
    container.visible = true;
  }

  public set playerSagInfo(info: { p?: ParticipantMovement; c?: ClusterCenter } | undefined) {
    this._playerSagInfo = info;
    this.updateSagStats();
  }

  public get playerSagInfo() {
    return this._playerSagInfo;
  }
}
